import { DotsThreeVerticalIcon } from '@phosphor-icons/react';
import { Dropdown } from 'antd';
import type { ReactNode } from 'react';

import styles from './action-menu.module.scss';

export type ActionMenuItem = {
  key: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
};

export type ActionMenuGroup = {
  label?: string;
  items: ActionMenuItem[];
};

export type ActionMenuProps = {
  groups: ActionMenuGroup[];
  children?: ReactNode;
  triggerLabel?: string;
};

function itemLabel(item: ActionMenuItem) {
  return (
    <span className={styles.itemBody}>
      <span className={styles.itemTitle}>{item.label}</span>
      {item.description ? <span className={styles.itemDescription}>{item.description}</span> : null}
    </span>
  );
}

export function ActionMenu({ groups, children, triggerLabel = 'Actions' }: ActionMenuProps) {
  const handlers = new Map(groups.flatMap((g) => g.items.map((i) => [i.key, i.onClick])));

  const items = groups.flatMap((group, index) => [
    ...(index > 0 ? [{ type: 'divider' as const, key: `divider-${index}` }] : []),
    ...(group.label ? [{ type: 'group' as const, key: `group-${index}`, label: group.label }] : []),
    ...group.items.map((item) => ({
      key: item.key,
      label: itemLabel(item),
      icon: item.icon,
      danger: item.danger,
      disabled: item.disabled,
    })),
  ]);

  return (
    <Dropdown
      trigger={['click']}
      menu={{ items, onClick: ({ key }) => handlers.get(key)?.() }}
      classNames={{ root: styles.popup }}
    >
      {children ?? (
        <button type="button" className={styles.trigger} aria-label={triggerLabel}>
          <DotsThreeVerticalIcon size={18} weight="bold" aria-hidden />
        </button>
      )}
    </Dropdown>
  );
}
